import React, { useEffect, useMemo, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import {
  createChargeRule,
  updateChargeRule,
} from "../../../../graphql/mutations";

import {
  Backdrop,
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Portal,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import {
  Button,
  Datagrid,
  ListContextProvider,
  ListToolbar,
  TextField,
  TopToolbar,
  useList,
  useNotify,
  FunctionField,
  ReferenceField,
  SelectField,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  ImportIcon,
  CloseIcon,
  CancelIcon,
} from "../../../../components/common/iconography";
import { Pagination } from "../../../../components/Pagination";
import { SearchField } from "../../../../components/common/SearchField";
import AccordionDatagrid from "../../../../components/common/AccordionDatagrid";

import { logError, logHistory } from "../../../../utils/appUtils";
import { ResourceType } from "../../../../utils/constants";
import {
  getRailEquipmentTypes,
  getTruckEquipmentTypes,
} from "../../../../utils/loadTypeUtils";

import { fetchActiveAccounts } from "../AccountUtils";
import {
  composeNewChargeRule,
  fetchChargeRules,
  getRuleScopeKey,
  normalizeFreeCars,
  validateChargeRule,
} from "./ChargeRuleUtils";
import { ChargeRuleUploadDialog } from "./ChargeRuleUploadDialog";

const useStyles = makeStyles((theme) => ({
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: theme.palette.common.white,
  },
  dialogContent: {
    height: "90vh",
  },
  dialogTitle: {
    display: "flex",
    paddingTop: "5px",
    paddingBottom: "5px",
    justifyContent: "space-between",
  },
}));

const AccountSearchField = (props) => {
  const { allAccounts, setAccounts, label, alwaysOn } = props;

  const handleSearch = (value) => {
    const searchTerm = value?.toLowerCase() || "";
    const filtered = allAccounts.filter(
      (acc) =>
        acc.displayName?.toLowerCase().includes(searchTerm) ||
        acc.businessName?.toLowerCase().includes(searchTerm) ||
        acc.lotNumberPrefix?.toLowerCase().includes(searchTerm) ||
        acc.addressState?.toLowerCase().includes(searchTerm),
    );
    setAccounts(filtered);
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
      searchOnChange={true}
    />
  );
};

// Heuristic: UUIDs are 36 chars with dashes; service names are not.
const looksLikeServiceName = (s) =>
  typeof s === "string" &&
  s.trim().length > 0 &&
  s.length < 100 &&
  !/^[0-9a-f-]{36}$/i.test(s.trim());

const getRowImportState = (
  rule,
  existingRules,
  existingServices,
  existingRuleKeysSet,
) => {
  const serviceName = (rule?.service?.name || rule?.serviceId || "").trim();
  const targetService = existingServices?.length
    ? existingServices.find((s) => (s?.name || "").trim() === serviceName)
    : null;
  // Only mark "No service" when we have a real name to match (not a raw serviceId/UUID from missing relation)
  const missingService =
    existingServices?.length > 0 &&
    looksLikeServiceName(serviceName) &&
    !targetService;
  const willOverride =
    !!targetService &&
    !!existingRuleKeysSet?.size &&
    existingRuleKeysSet.has(getRuleScopeKey(rule, targetService.id));

  // Reuse same conflict detection path used by CSV import
  // (validateChargeRule returns blocking issues as _error).
  let hasConflict = false;
  if (!willOverride && targetService && Array.isArray(existingRules)) {
    const candidate = {
      ...rule,
      serviceId: targetService.id,
      active: "true",
    };
    const validation = validateChargeRule(candidate, existingRules);
    hasConflict = !!validation?._error;
  }

  const willCreate = !!targetService && !willOverride && !hasConflict;
  return { missingService, willOverride, willCreate, hasConflict };
};

const getSeverityRowStyle = (theme, paletteKey) => {
  const isDarkTheme =
    (theme.palette.mode || theme.palette.type || "").toLowerCase() === "dark";
  const backgroundColor = isDarkTheme
    ? theme.palette[paletteKey].dark
    : theme.palette[paletteKey].light;
  return {
    backgroundColor,
    color: theme.palette.getContrastText(backgroundColor),
  };
};

const AccountRulesExpand = ({
  accountId,
  onSelectedChange,
  existingRules = [],
  existingServices = [],
}) => {
  const notify = useNotify();
  const theme = useTheme();
  const [rulesByAccount, setRulesByAccount] = useState({});
  const [rulesLoading, setRulesLoading] = useState({});
  const equipmentTypes = useMemo(() => {
    return [...getRailEquipmentTypes(), ...getTruckEquipmentTypes()];
  }, []);

  const loadingRules = !!rulesLoading[accountId];
  const accountRules = rulesByAccount[accountId] || [];

  const existingRuleKeysSet = useMemo(() => {
    if (!existingRules?.length) return new Set();
    return new Set(
      existingRules.map((r) => getRuleScopeKey(r, r.serviceId)).filter(Boolean),
    );
  }, [existingRules]);

  const hasOverlapOrSkip = useMemo(() => {
    return existingRuleKeysSet.size > 0 || (existingServices?.length ?? 0) > 0;
  }, [existingRuleKeysSet.size, existingServices?.length]);

  const loadRules = async () => {
    if (!accountId) return;
    if (rulesByAccount[accountId]?.length || rulesLoading[accountId]) return;

    try {
      setRulesLoading((m) => ({ ...m, [accountId]: true }));
      const data = await fetchChargeRules(accountId);
      setRulesByAccount((m) => ({ ...m, [accountId]: data || [] }));
    } catch (error) {
      logError("chargeRules:AccountRulesExpand:loadRules", error, notify);
    } finally {
      setRulesLoading((m) => ({ ...m, [accountId]: false }));
    }
  };

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const ruleIds = accountRules.map((r) => r.id);

  // Show all rules for the selected account without pagination.
  // (The account list itself remains paginated.)
  const rulesPerPage = Math.max(ruleIds.length, 1);

  const rulesListContext = useList({
    data: accountRules,
    ids: ruleIds,
    loaded: !loadingRules,
    loading: loadingRules,
    basePath: "/chargeRules",
    resource: "chargeRules",
    // Prevent chargeRules list params (like perPage=10) from being reused here.
    // We want to show *all* rules for the selected account in this import dialog.
    storeKey: false,
    page: 1,
    perPage: rulesPerPage,
    sort: { field: "event", order: "ASC" },
  });

  useEffect(() => {
    const ids = rulesListContext.selectedIds || [];
    onSelectedChange && onSelectedChange(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesListContext.selectedIds]);

  return (
    <ListContextProvider value={rulesListContext}>
      <Datagrid
        rowClick="toggleSelection"
        hasBulkActions
        rowStyle={(row) => {
          const { missingService, willCreate, hasConflict } = getRowImportState(
            row,
            existingRules,
            existingServices,
            existingRuleKeysSet,
          );
          if (missingService) return getSeverityRowStyle(theme, "warning");
          if (hasConflict) return getSeverityRowStyle(theme, "error");
          if (willCreate) return getSeverityRowStyle(theme, "success");
          return {};
        }}
      >
        <TextField source="event" label="Event" />
        <FunctionField
          source="service.name"
          label="Service"
          render={(r) => r?.service?.name || r.serviceId}
        />
        <TextField source="carrierType" label="Carrier Type" />
        <SelectField
          source="carrierEquipmentTypeId"
          label="Equipment"
          choices={equipmentTypes}
          optionText="name"
          optionValue="id"
        />
        <ReferenceField
          source="reloadYardCommodityId"
          reference="reloadYardCommoditys"
          label="Load Type"
          link={false}
        >
          <TextField source="commodity.name" />
        </ReferenceField>
        <TextField source="billingUOM" label="UOM" />
        <TextField source="execution" label="Execution" />
        {hasOverlapOrSkip && (
          <FunctionField
            label="Overlap"
            render={(row) => {
              const { missingService, willOverride, willCreate, hasConflict } =
                getRowImportState(
                  row,
                  existingRules,
                  existingServices,
                  existingRuleKeysSet,
                );
              if (missingService) return "No service";
              if (hasConflict) return "Conflict";
              if (willOverride) return "Replace";
              if (willCreate) return "Add";
              return "";
            }}
          />
        )}
      </Datagrid>
    </ListContextProvider>
  );
};

const ImportFromAccountDialog = ({
  targetAccount,
  open,
  onClose,
  onSubmit,
  existingRules = [],
  existingServices = [],
}) => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [allAccounts, setAllAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [selectedRuleIds, setSelectedRuleIds] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        let data = await fetchActiveAccounts(notify);
        if (targetAccount?.id) {
          data = data.filter((a) => a.id !== targetAccount.id);
        }
        setAllAccounts(data);
        setAccounts(data);
      } finally {
        setLoading(false);
      }
    };

    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetAccount?.id]);

  const handleClose = () => {
    setSelectedRuleIds([]);
    setExpandedAccountId(null);
    onClose && onClose();
  };

  const ids = useMemo(() => accounts.map((a) => a.id), [accounts]);

  const accountListContext = useList({
    data: accounts,
    ids,
    loaded: !loading,
    loading,
    basePath: "/accounts",
    resource: "accounts",
    perPage: 10,
    sort: { field: "displayName", order: "ASC" },
  });

  const handleConfirm = async () => {
    if (!expandedAccountId) return;
    if (selectedRuleIds.length === 0) return;

    try {
      // load all rules for the expanded account
      const allRules = await fetchChargeRules(expandedAccountId);

      const selectedRules = allRules.filter((r) =>
        selectedRuleIds.includes(r.id),
      );

      const account = accounts.find((a) => a.id === expandedAccountId);

      onSubmit({ account, rules: selectedRules });
    } catch (e) {
      logError(
        "chargeRules:ImportChargeRulesMenuButton:handleConfirm",
        e,
        notify,
      );
    }
  };

  return (
    <Dialog maxWidth="xl" open={open} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <h2>Import Account Rules</h2>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        <ListContextProvider value={accountListContext}>
          <ListToolbar
            filters={[
              <AccountSearchField
                label="Account Search"
                allAccounts={allAccounts}
                setAccounts={setAccounts}
                alwaysOn
              />,
            ]}
            actions={<TopToolbar />}
          />

          <AccordionDatagrid
            expand={
              <AccountRulesExpand
                accountId={expandedAccountId}
                onSelectedChange={setSelectedRuleIds}
                existingRules={existingRules}
                existingServices={existingServices}
              />
            }
            onToggleExpand={(nextId) => {
              setExpandedAccountId(nextId);
              // clear selected rules when switching accounts
              setSelectedRuleIds([]);
            }}
            cleanupSelectionResource="chargeRules"
          >
            <TextField source="displayName" label="Display Name" />
            <TextField source="businessName" label="Account" />
            <TextField source="lotNumberPrefix" label="Lot Prefix" />
            <TextField source="addressState" label="State" />
          </AccordionDatagrid>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Pagination {...accountListContext} />
          </div>
        </ListContextProvider>
      </DialogContent>

      <DialogActions>
        <div style={{ flex: 1 }}>
          <Typography variant="body2">
            Selected: {selectedRuleIds.length}
          </Typography>
        </div>
        <Button label="Cancel" color="default" onClick={handleClose}>
          <CancelIcon />
        </Button>

        <Button
          label="Import"
          color="primary"
          onClick={handleConfirm}
          disabled={!selectedRuleIds.length}
        >
          <ImportIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ImportChargeRulesMenuButton = ({
  account,
  existingRules = [],
  existingServices = [],
  handleRefresh,
  disabled,
  enableCSV = true,
  onCreateMerge,
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const form = useForm();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (!enableCSV) {
      setSelectOpen(true);
      return;
    }
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleFromAccount = () => {
    setSelectOpen(true);
    handleClose();
  };

  const handleCSV = () => {
    if (!enableCSV) return;
    setUploadOpen(true);
    handleClose();
  };

  const getSkippedRulesMessage = (skipped) => {
    const missingServiceName = Number(skipped?.missingServiceName) || 0;
    const missingTargetService = Number(skipped?.missingTargetService) || 0;
    const total = missingServiceName + missingTargetService;
    if (total <= 0) return null;

    const parts = [];
    if (missingServiceName)
      parts.push(`${missingServiceName} missing service name`);
    if (missingTargetService)
      parts.push(
        `${missingTargetService} missing matching service on target account`,
      );

    return `Imported with warnings: skipped ${total} rule${
      total === 1 ? "" : "s"
    } (${parts.join(", ")}).`;
  };

  const importFromAccount = async (payload) => {
    try {
      setIsSaving(true);
      let sourceAccount = payload?.account || payload;
      let sourceRules = payload?.rules;
      if (!sourceRules) {
        // fallback: fetch all rules if a plain account was passed
        sourceRules = await fetchChargeRules(sourceAccount.id);
      }

      if (!account) {
        // CREATE flow: merge into local form state (by event + serviceName)
        const skipped = {
          missingServiceName: 0,
          missingTargetService: 0,
        };

        const normalize = (r) => ({
          event: r.event,
          serviceName: r.service?.name || "",
          billingUOM: r.billingUOM,
          reloadYardCommodityId: r.reloadYardCommodityId || null,
          carrierType: r.carrierType || "",
          carrierEquipmentTypeId: r.carrierEquipmentTypeId || null,
          areaId: r.areaId || null,
          zoneId: r.zoneId || null,
          minCharge: r.minCharge || null,
          maxCharge: r.maxCharge || null,
          execution: r.execution || "",
          frequency: r.frequency || "",
          feeAccrual: r.feeAccrual || "",
          freeDaysPolicy: r.freeDaysPolicy || "",
          freeDays: r.freeDays ?? null,
          freeCars: r.freeCars ?? null,
        });

        const base = existingRules.map(normalize);
        let merged = [...base];
        sourceRules.forEach((rule) => {
          const item = normalize(rule);
          const key = `${item.event}_${item.serviceName}`;
          const idx = merged.findIndex(
            (m) => `${m.event}_${m.serviceName}` === key,
          );
          if (idx >= 0) merged[idx] = item;
          else if (item.serviceName) merged.push(item);
          else skipped.missingServiceName += 1;
        });
        onCreateMerge && onCreateMerge(merged);

        const skippedMsg = getSkippedRulesMessage(skipped);
        notify(
          skippedMsg || "Rules imported successfully",
          skippedMsg ? "warning" : "success",
        );
      } else {
        // EDIT flow: create/update via API on target account
        const skipped = {
          missingServiceName: 0,
          missingTargetService: 0,
        };

        const oldRulesMap = existingRules?.length
          ? existingRules.reduce((acc, r) => {
              acc[`${r.event}_${r.serviceId}`] = r;
              return acc;
            }, {})
          : {};

        const results = await Promise.allSettled(
          sourceRules.map((src) => {
            const serviceName = src.service?.name || "";
            if (!serviceName) {
              return Promise.resolve({
                skipped: true,
                reason: "missing serviceName",
              });
            }
            const targetService = existingServices.find(
              (s) => s.name === serviceName,
            );
            if (!targetService) {
              // skip if target doesn't have this service
              return Promise.resolve({
                skipped: true,
                reason: "missing service",
              });
            }
            const ruleKey = `${src.event}_${targetService.id}`;
            const oldRule = oldRulesMap[ruleKey];

            if (oldRule) {
              const input = {
                ...oldRule,
                // keep identity fields from oldRule; apply values from source
                billingUOM: src.billingUOM,
                reloadYardCommodityId: src.reloadYardCommodityId || null,
                carrierType: src.carrierType || "",
                carrierEquipmentTypeId: src.carrierEquipmentTypeId || null,
                areaId: src.areaId || null,
                zoneId: src.zoneId || null,
                minCharge: src.minCharge || null,
                maxCharge: src.maxCharge || null,
                execution: src.execution || "",
                frequency: src.frequency || "",
                feeAccrual: src.feeAccrual || "",
                freeDaysPolicy: src.freeDaysPolicy || "",
                freeDays: src.freeDays ?? null,
                freeCars: normalizeFreeCars(src),
                serviceId: targetService.id,
              };

              delete input.createdAt;
              delete input.history;
              delete input.service;
              delete input.updatedAt;
              return API.graphql(graphqlOperation(updateChargeRule, { input }));
            }

            const item = {
              event: src.event,
              serviceName,
              billingUOM: src.billingUOM,
              reloadYardCommodityId: src.reloadYardCommodityId || null,
              carrierType: src.carrierType || "",
              carrierEquipmentTypeId: src.carrierEquipmentTypeId || null,
              areaId: src.areaId || null,
              zoneId: src.zoneId || null,
              minCharge: src.minCharge || null,
              maxCharge: src.maxCharge || null,
              execution: src.execution || "",
              frequency: src.frequency || "",
              feeAccrual: src.feeAccrual || "",
              freeDaysPolicy: src.freeDaysPolicy || "",
              freeDays: src.freeDays ?? null,
              freeCars: src.freeCars ?? null,
            };
            const input = composeNewChargeRule(item, existingServices, account);
            return API.graphql(graphqlOperation(createChargeRule, { input }));
          }),
        );

        const resError = results.find(
          (itm) => itm.status === "rejected" && !itm?.reason?.skipped,
        );
        if (resError) throw resError.reason;

        const newRules = [];
        const updatedRuleMap = {};
        results.forEach((result) => {
          if (result.status !== "fulfilled") return;
          if (result.value?.skipped) {
            if (result.value.reason === "missing serviceName")
              skipped.missingServiceName += 1;
            else if (result.value.reason === "missing service")
              skipped.missingTargetService += 1;
            return;
          }
          const payload = result.value?.data;
          if (payload?.createChargeRule)
            newRules.push(payload.createChargeRule);
          if (payload?.updateChargeRule)
            updatedRuleMap[payload.updateChargeRule.id] =
              payload.updateChargeRule;
        });

        // update form state for immediate UI feedback
        let newChargeRuleList = existingRules.map((itm) =>
          updatedRuleMap[itm.id] ? updatedRuleMap[itm.id] : itm,
        );
        newChargeRuleList = [...newChargeRuleList, ...newRules];
        form.change("chargeRules.items", newChargeRuleList);

        // history logs
        for (const rule of newRules) {
          const historyNote = {
            parentId: rule.id,
            parentType: ResourceType.CHARGE_RULE,
            text: "Rule Created.",
          };
          logHistory(historyNote);
        }
        for (const rule of Object.values(updatedRuleMap)) {
          const historyNote = {
            parentId: rule.id,
            parentType: ResourceType.CHARGE_RULE,
            text: "Billing Rules bulk update",
          };
          logHistory(historyNote);
        }

        const skippedMsg = getSkippedRulesMessage(skipped);
        notify(
          skippedMsg || "Rules imported successfully",
          skippedMsg ? "warning" : "success",
        );
        handleRefresh && handleRefresh();
      }
    } catch (error) {
      logError(
        "chargeRules:ImportChargeRulesMenuButton:importFromAccount",
        error,
        notify,
      );
    } finally {
      setIsSaving(false);
      setSelectOpen(false);
    }
  };

  return (
    <>
      <MuiButton
        id="import-charge-rules-button"
        aria-controls={
          enableCSV && open ? "import-charge-rules-menu" : undefined
        }
        aria-haspopup={enableCSV ? "true" : undefined}
        aria-expanded={enableCSV && open ? "true" : undefined}
        onClick={handleClick}
        color="primary"
        size="small"
        disabled={disabled}
        className={classes.actionButton}
        startIcon={<ImportIcon />}
      >
        Import
      </MuiButton>

      {enableCSV && (
        <Menu
          id="import-charge-rules-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          getContentAnchorEl={null}
        >
          <MenuItem onClick={handleFromAccount}>From Account</MenuItem>
          <MenuItem onClick={handleCSV}>CSV</MenuItem>
        </Menu>
      )}

      {selectOpen && (
        <ImportFromAccountDialog
          targetAccount={account}
          open={selectOpen}
          onClose={() => setSelectOpen(false)}
          onSubmit={importFromAccount}
          existingRules={existingRules}
          existingServices={existingServices}
        />
      )}

      {uploadOpen && (
        <ChargeRuleUploadDialog
          account={account}
          existingRules={existingRules}
          existingServices={existingServices}
          uploadOpen={uploadOpen}
          setUploadOpen={setUploadOpen}
          handleRefresh={handleRefresh}
        />
      )}

      <Portal>
        <Backdrop open={isSaving} className={classes.backdrop}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Portal>
    </>
  );
};
